import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import Popper from 'popper.js';
import { Subject } from 'rxjs';

import { BooleanConverter, InputConverter } from '../../../inputConverter';
import { GisToolsService } from '../../../services/gis-tools.service';
import { BaseComponent } from '../../base.component';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-rms-select',
    styleUrls: ['../../sharedStyles/base.scss', './rms-select.component.scss'],
    templateUrl: './rms-select.component.html'
})
export class RmsSelectComponent extends BaseComponent implements AfterViewInit {
    @ViewChild('rmsSelectToggle') private readonly rmsSelectToggleRef: ElementRef<HTMLDivElement>;
    @ViewChild('rmsSelectContainer') private readonly rmsSelectContainerRef: ElementRef<HTMLDivElement>;

    private rmsSelectPopperRef: Popper;

    @Input() @InputConverter(BooleanConverter)
    get showRmsSelect() { return this._showRmsSelect; }
    set showRmsSelect(value) {
        this._showRmsSelect = value;
        this._showRmsSelect ? this.onShow() : this.onHide();
    }
    private _showRmsSelect: boolean;

    @Input()
    get position() { return this._position; }
    set position(value) {
        if (!this._position || value.latitude !== this._position.latitude || value.longitude !== this._position.longitude) {
            this.setRms(value.latitude, value.longitude);
        }
        this._position = value;
    }
    private _position: any;

    county: any = null;
    route: any = null;
    segment: any = null;
    offset: number = 0;

    counties: any[];
    routes: any[];
    filteredRoutes: any[];
    segments: any[];
    filteredSegments: any[];

    offsetChange: Subject<number>;
    buffer: number = 100;

    @Output()
    readonly rmsChange: EventEmitter<any> = new EventEmitter<any>();

    constructor(
        _vcr: ViewContainerRef,
        private readonly cdr: ChangeDetectorRef,
        private readonly gisToolsService: GisToolsService) {
        super(_vcr, true);
    }

    async ngAfterViewInit() {
        this.counties = await this.gisToolsService.getAllCounties('countyCode, name');
        this.rmsSelectPopperRef = new Popper(this.rmsSelectToggleRef.nativeElement,
            this.rmsSelectContainerRef.nativeElement,
            {
                placement: 'bottom-end',
                positionFixed: true
            });
    }

    private onShow() {
        this.rmsSelectPopperRef.update();
        window.addEventListener('mousedown', this.outsideClickHandler);
        window.addEventListener('dragstart', this.outsideClickHandler);
        this.rmsSelectToggleRef.nativeElement.addEventListener('mousedown', this.insideClickHandler);
        this.rmsSelectContainerRef.nativeElement.addEventListener('mousedown', this.insideClickHandler);
    }

    private onHide() {
        window.removeEventListener('mousedown', this.outsideClickHandler);
        window.removeEventListener('dragstart', this.outsideClickHandler);
        this.rmsSelectToggleRef.nativeElement.removeEventListener('mousedown', this.insideClickHandler);
        this.rmsSelectContainerRef.nativeElement.removeEventListener('mousedown', this.insideClickHandler);
    }

    private readonly outsideClickHandler = () => {
        this.showRmsSelect = false;
    }

    private readonly insideClickHandler = (e: MouseEvent) => {
        e.stopPropagation();
    }

    async onCountyChange(county) {
        this.county = county;
        this.route = null;
        this.segment = null;
        if (county && county.countyCode) {
            this.routes = await this.gisToolsService.getStateRoutesByCounty(county.countyCode, 'countyCode, stateRouteNumber, streetNames');
            this.filteredRoutes = this.routes.filter(r => r.countyCode === county.countyCode);
        }
    }

    async onRouteChange(route) {
        this.route = route;
        this.segment = null;
        if (route && route.stateRouteNumber) {
            this.segments = await this.gisToolsService.getSegmentsByCountyAndRoute(this.county.countyCode, route.stateRouteNumber, 'countyCode, stateRouteNumber, segmentNumber, streetName, segmentLength');
            this.filteredSegments = this.segments.filter(s => {
                return s.countyCode === route.countyCode && s.stateRouteNumber === route.stateRouteNumber && s.stateRouteNumber === route.stateRouteNumber;
            });
        }
    }

    onSegmentChange(segment) {
        this.segment = segment;
        this.setOffset(0);
    }

    onOffsetChange(offset) {
        this.setOffset(offset);
    }

    private setOffset(offset) {
        if (!offset || Number.isNaN(offset) || offset < 0) {
            this.offset = 0;
        } else if (offset > this.segment.segmentLength) {
            this.offset = this.segment.segmentLength;
        } else {
            this.offset = offset;
        }

        this.rmsChange.emit({
            countyCode: this.county.countyCode,
            stateRouteNumber: this.route.stateRouteNumber,
            segmentNumber: this.segment.segmentNumber,
            offset: this.offset.toString()
        });
    }
    async setRms(latitude: number, longitude: number) {
        const rmsOffsets = await this.gisToolsService.getOffsetForLatLong(latitude, longitude, this.buffer, 'countyCode, stateRouteNumber, segmentNumber, offset');
        if (rmsOffsets && rmsOffsets.length > 0) {
            const countyChange = !this.county || this.county.countyCode !== rmsOffsets[0].countyCode;
            const routeChange = !this.route || this.route.stateRouteNumber !== rmsOffsets[0].stateRouteNumber;
            if (countyChange) {
                this.county = this.counties.find(c => c.countyCode === rmsOffsets[0].countyCode);
                await this.onCountyChange(this.county);
            }
            if (countyChange || routeChange) {
                this.route = this.filteredRoutes.find(r => r.stateRouteNumber === rmsOffsets[0].stateRouteNumber);
                await this.onRouteChange(this.route);
            }
            this.segment = this.filteredSegments.find(s => s.segmentNumber === rmsOffsets[0].segmentNumber);
            this.offset = rmsOffsets[0].offset;
            this.cdr.detectChanges();
        }
    }
}
